import { TemplatesApi } from '../../../../../api/templatesApi';
import { SnackbarParameters } from '../../../../../interfaces/Snackbar';
import { Template } from '../../../../../interfaces/Templates';
import withSnackbar from '../../../../../shared/components/withSnackbar';
import {
	SovosButton,
	SovosDialog,
	SovosDialogActions,
	SovosDialogContent,
	SovosDialogTitle,
	SovosTextField,
} from 'mosaic-react';
import { useEffect, useState } from 'react';

interface RenameTemplateDialogProps {
	isDialogOpen: boolean;
	closeDialog: () => void;
	template: Template;
	forceReloadingTemplate: () => void;
	showSnackbar: (value: SnackbarParameters) => void;
}

const RenameTemplateDialog = (props: RenameTemplateDialogProps) => {
	const { isDialogOpen, closeDialog, template, forceReloadingTemplate, showSnackbar } = props;
	const [newTemplateName, setNewTemplateName] = useState<string>('');
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [errorTemplateName, setErrorTemplateName] = useState<string>('');

	const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		setNewTemplateName(event.target.value);
		setErrorTemplateName(!event.target.value ? 'Template Name is required.' : '');
	};

	useEffect(() => {
		if (isDialogOpen) {
			setNewTemplateName(template.name);
			setErrorTemplateName('');
		}
	}, [template, isDialogOpen]);

	const handleClose = (): void => {
		if (!isSaving) {
			closeDialog();
		}
	};

	const handleSave = (): void => {
		if (!errorTemplateName && isDialogOpen) {
			if (newTemplateName !== template.name) {
				setIsSaving(true);
				TemplatesApi.renameTemplate(template.id, newTemplateName)
					.then(response => {
						switch (response.status) {
							case 200: {
								closeDialog();
								forceReloadingTemplate();
								showSnackbar({ message: 'Template name updated', variant: 'confirmation' });
								break;
							}
							case 400: {
								setErrorTemplateName(response.data.errors[0].message);
								break;
							}
							default: {
								closeDialog();
								forceReloadingTemplate();
								showSnackbar({ message: 'Unable to rename template. Please try again later.', variant: 'error' });
								break;
							}
						}
					})
					.catch(err => setErrorTemplateName(err.response.data.errors[0].message))
					.finally(() => setIsSaving(false));
			} else {
				closeDialog();
			}
		}
	};

	return (
		<SovosDialog open={isDialogOpen} onClose={handleClose}>
			<SovosDialogTitle>Rename template</SovosDialogTitle>
			<SovosDialogContent>
				<p>Enter a new name below. Once saved, you&lsquo;ll be able to apply this template to future imports.</p>
				<div>
					<SovosTextField
						onChange={handleNameChange}
						placeholder='Template Name'
						value={newTemplateName}
						error={Boolean(errorTemplateName)}
						helperText={errorTemplateName}
						autoFocus
						fullWidth
					/>
				</div>
			</SovosDialogContent>
			<SovosDialogActions>
				<SovosButton variant='text' onClick={handleClose} disabled={isSaving}>
					Cancel
				</SovosButton>
				<SovosButton onClick={handleSave} disabled={isSaving}>
					Save Template
				</SovosButton>
			</SovosDialogActions>
		</SovosDialog>
	);
};

export default withSnackbar(RenameTemplateDialog);
